import { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

import { PriceBlock, PriceBlockPricing } from './PriceBlock';

interface Props {
  /** Etiqueta de la tarifa tal como la publica el proveedor ("Básico"). */
  label: ReactNode;
  /** Letra chica: flexibilidad, cambios, el link de condiciones. */
  detail?: ReactNode;
  pricing?: PriceBlockPricing;
  /** Texto contractual cuando el producto todavía no tiene precio. */
  unpricedLabel?: ReactNode;
  /**
   * Qué decir del precio comisionable EN LA FILA. `null` lo calla: con varias
   * filas del mismo proveedor, el aviso va una vez en la columna.
   */
  grossNote?: ReactNode;
  /** El verbo real de la acción ("Elegir", "Reservar"). */
  actionLabel: string;
  onAction: () => void;
  /**
   * Bloqueada ≠ muda: si la fila no se puede elegir, el motivo va pegado al
   * botón. Un botón gris sin explicación manda al asesor a adivinar.
   *
   * Es un nodo y no un string para que el caller pueda darle el color que le
   * corresponde: no es lo mismo una cotización vencida que una tarifa que no
   * aplica a este grupo de pasajeros.
   */
  disabledReason?: ReactNode;
  /** Un importe en 0 puede ser real (una banda de infante que no paga). */
  zeroLabel?: string;
  /** Condiciones desplegadas. Van debajo de la fila, no en otra columna. */
  children?: ReactNode;
}

/**
 * Una fila de la franja de tarifas. La fila entera reacciona al hover —tinte y
 * borde izquierdo— para guiar el ojo, pero el único click es el botón: un
 * target invisible sobre la fila falla en descubribilidad y en teclado.
 *
 * En hover el botón pasa de contorno a lleno, así la fila dice cuál se va a
 * elegir ANTES del click.
 */
export function FareRow({
  label,
  detail,
  pricing,
  unpricedLabel,
  grossNote,
  actionLabel,
  onAction,
  disabledReason,
  zeroLabel,
  children,
}: Props) {
  const disabled = Boolean(disabledReason);

  return (
    <Box
      sx={(theme) => ({
        py: '7px',
        pl: '10px',
        borderLeft: '2px solid transparent',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '& + &': {
          borderTop: 1,
          borderTopColor: 'divider',
        },
        '&:hover': {
          bgcolor: theme.palette.action.hover,
          borderLeftColor: theme.palette.primary.main,
        },
        '&:hover .RuteroFareRow-action': {
          bgcolor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        // El importe de la franja pesa como el de la columna: son la misma
        // comparación, en dos alturas distintas de la card.
        '& .MuiTypography-body2': { fontWeight: 600 },
      })}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 150px 104px' },
          gap: { xs: 0.75, sm: 1.75 },
          alignItems: 'center',
        }}
      >
        <Box minWidth={0}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
            {label}
          </Typography>
          {detail && (
            <Typography
              component="span"
              color="text.secondary"
              sx={{ fontSize: 12, ml: 0.5 }}
            >
              {detail}
            </Typography>
          )}
        </Box>

        {/* `display: contents` en desktop: el envoltorio desaparece y precio y
            botón vuelven a ser dos columnas de la grilla. En mobile es la fila
            que cierra la tarifa, con el mismo orden que la card. */}
        <Box
          sx={(rowTheme) => ({
            display: { xs: 'flex', sm: 'contents' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            minHeight: { xs: 44, sm: 'auto' },
            // Solo mientras la fila está apilada: el primer hijo es el precio,
            // que ahí abre por la izquierda. Desde `sm` vuelve a ser una
            // columna alineada a la derecha.
            [rowTheme.breakpoints.down('sm')]: {
              '& > .MuiBox-root:first-of-type': { textAlign: 'left' },
            },
          })}
        >
          {pricing ? (
            <PriceBlock
              pricing={pricing}
              grossNote={grossNote}
              zeroLabel={zeroLabel}
              size="small"
              align="right"
            />
          ) : (
            unpricedLabel
          )}

          <Box textAlign="right">
            <Button
              className="RuteroFareRow-action"
              variant="outlined"
              disabled={disabled}
              onClick={onAction}
              sx={{
                minWidth: 88,
                height: 26,
                minHeight: { xs: 44, sm: 26 },
                px: 1.5,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                transition: 'background-color 120ms ease, transform 160ms ease',
                '&:active': { transform: 'scale(.97)' },
                '@media (prefers-reduced-motion: reduce)': {
                  '&:active': { transform: 'none' },
                },
              }}
            >
              {actionLabel}
            </Button>
            {disabledReason && (
              <Typography
                color="text.secondary"
                sx={{ display: 'block', fontSize: 10.5, mt: '2px' }}
              >
                {disabledReason}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {children}
    </Box>
  );
}
